import classNames from 'classnames';
import moment from 'moment';
import React, { Fragment, useMemo } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { makeStyles } from '@mui/styles';

import {
  CCMServiceProvider,
  ProgramExtensionStatusReason,
} from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  BaseEnum,
  MemberProgramExtension,
  ProgramExtension,
  ProgramExtensionStatus,
} from '@vestahealthcare/common/models';
import { DATE_FORMAT } from '@vestahealthcare/common/utils/constants';

import { DateTimePicker, IconButton, Select, TextInput } from 'styleguide-v2';

import { excludeOtherValues } from 'dash/src/utils/dataUtils';

import { useCommonStyles } from '../../styles';

type Props = {
  data: {
    programExtensions: ProgramExtension[];
  };
  disabled?: boolean;
  programExtensions: MemberProgramExtension[];
  onChange: (pes: MemberProgramExtension[]) => void;
  submitted?: boolean;
};

const I18N_NAMESPACE = 'personalDetails.programDetailsCard.modal';

const useStyles = makeStyles({
  marginDate: {
    marginTop: '0.5rem',
  },
});

export const MemberProgramEditProgramExtensions = ({
  data,
  disabled,
  programExtensions,
  onChange,
  submitted,
}: Props) => {
  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const hasVPC = useMemo(
    () =>
      programExtensions.find(
        ({ programExtension, status }) =>
          programExtension?.id === ProgramExtension.VPC &&
          status?.id !== ProgramExtensionStatus.DISENROLLED,
      ),
    [programExtensions],
  );

  return (
    <>
      <h4 className="grid-span-12">
        {translate(`${I18N_NAMESPACE}.programExtensions`)}
      </h4>
      {programExtensions?.map((mpe, idx) => (
        <Fragment
          key={`program-extension-${mpe.programExtension?.id || 0}-${idx}`}
        >
          <Select
            className="grid-span-6"
            disableClearable
            disabled={disabled}
            error={submitted && !mpe.programExtension}
            getItemLabel={(pe: ProgramExtension) => pe.abbr}
            label={
              idx === 0 ? (
                <span className="flex middle" style={{ height: '17.14px' }}>
                  {translate(`${I18N_NAMESPACE}.programExtensions`)}{' '}
                  {!disabled && (
                    <button
                      className={classNames(
                        'flex middle',
                        commonStyles.addLink,
                      )}
                      onClick={(evt) => {
                        evt.preventDefault();
                        onChange([
                          ...(programExtensions || []),
                          new MemberProgramExtension({}),
                        ]);
                      }}
                    >
                      <AddIcon
                        className={commonStyles.addIcon}
                        fontSize="small"
                      />
                      {translate(`${I18N_NAMESPACE}.programExtensionsAdd`)}
                    </button>
                  )}
                </span>
              ) : undefined
            }
            placeholder={translate('global.select')}
            onChange={(programExtension: ProgramExtension) => {
              programExtensions[idx].programExtension = programExtension;
              if (programExtension.id === ProgramExtension.CCM) {
                programExtensions[idx].serviceProvider =
                  CCMServiceProvider.VESTA;
              }
              onChange([...programExtensions]);
            }}
            items={excludeOtherValues(
              data.programExtensions,
              programExtensions.map(({ programExtension }) => programExtension),
              mpe.programExtension,
            )}
            renderOption={(pe: ProgramExtension) => pe.name}
            value={mpe.programExtension}
          />
          <Select
            className="grid-span-5"
            disabled={!mpe.programExtension || disabled}
            disableClearable
            error={submitted && !mpe.status}
            getItemLabel={BaseEnum.getLabel}
            label={
              idx === 0
                ? translate(`${I18N_NAMESPACE}.programExtensionStatus`)
                : ''
            }
            placeholder={translate(
              `${I18N_NAMESPACE}.programExtensionStatusPlaceholder`,
            )}
            onChange={(status: ProgramExtensionStatus) => {
              programExtensions[idx].status = status;
              onChange([...programExtensions]);
            }}
            items={
              data.programExtensions
                ?.find(({ id }) => id === mpe.programExtension?.id)
                ?.statuses?.sort(ProgramExtensionStatus.sort) || []
            }
            value={mpe.status}
            required
          />
          <IconButton
            className={classNames(
              commonStyles.icon,
              idx === 0 && commonStyles.firstIcon,
              'grid-span-1',
            )}
            disabled={
              disabled ||
              (programExtensions &&
                programExtensions.length < 2 &&
                !programExtensions[0]?.programExtension &&
                !programExtensions[0]?.status)
            }
            size="small"
            onClick={() => {
              if (programExtensions && programExtensions.length < 2) {
                onChange([new MemberProgramExtension({})]);
              } else {
                programExtensions?.splice(idx, 1);
                onChange([...programExtensions]);
              }
            }}
          >
            <DeleteOutlineIcon fontSize="large" />
          </IconButton>
          {mpe.programExtension?.id === ProgramExtension.CCM && (
            <Select
              className="grid-span-11"
              disabled={!hasVPC || disabled}
              getItemLabel={(item: CCMServiceProvider) => item?.toString()}
              placeholder={translate(
                `${I18N_NAMESPACE}.programExtensionServiceProvider`,
              )}
              onChange={(serviceProvider: CCMServiceProvider) => {
                programExtensions[idx].serviceProvider = serviceProvider;
                onChange([...programExtensions]);
              }}
              items={CCMServiceProvider.asArray}
              value={mpe.serviceProvider}
            />
          )}
          {mpe.status?.id === ProgramExtensionStatus.HOLD && (
            <>
              <Select
                className="grid-span-6"
                disabled={disabled}
                error={submitted && !mpe.statusReason}
                getItemLabel={(item: ProgramExtensionStatusReason) =>
                  item?.toString()
                }
                placeholder={translate(
                  `${I18N_NAMESPACE}.programExtensionStatusReason`,
                )}
                onChange={(statusReason: ProgramExtensionStatusReason) => {
                  programExtensions[idx].statusReason = statusReason;
                  onChange([...programExtensions]);
                }}
                items={ProgramExtensionStatusReason.asArray}
                value={mpe.statusReason}
                required
              />
              {mpe.statusReason === ProgramExtensionStatusReason.OTHER && (
                <TextInput
                  className="grid-span-6"
                  disabled={disabled}
                  error={submitted && !mpe.statusReasonOther}
                  placeholder={translate('global.other')}
                  onChange={(statusReasonOther?: string) => {
                    programExtensions[idx].statusReasonOther =
                      statusReasonOther || '';
                    onChange([...programExtensions]);
                  }}
                  value={mpe.statusReasonOther}
                  required
                />
              )}
              <DateTimePicker
                className={classNames('grid-span-6', styles.marginDate)}
                disabled={
                  disabled ||
                  mpe.statusReason ===
                    ProgramExtensionStatusReason.DISENROLL_REQUEST
                }
                placeholder={translate(
                  `${I18N_NAMESPACE}.programExtensionReleaseDate`,
                )}
                onChange={(releaseDate?: Date) => {
                  programExtensions[idx].releaseDate = releaseDate
                    ? moment(releaseDate.getTime()).format(DATE_FORMAT)
                    : '';
                  onChange([...programExtensions]);
                }}
                minDate={moment().add(1, 'day').toDate()}
                value={
                  mpe.statusReason !==
                    ProgramExtensionStatusReason.DISENROLL_REQUEST &&
                  programExtensions[idx].releaseDate
                    ? moment(
                        programExtensions[idx].releaseDate,
                        DATE_FORMAT,
                      )?.toDate()
                    : undefined
                }
              />
            </>
          )}
          {(mpe.status?.id === ProgramExtensionStatus.HOLD ||
            mpe.programExtension?.id === ProgramExtension.CCM) &&
            idx !== programExtensions?.length - 1 && (
              <hr className="separator grid-span-12" />
            )}
        </Fragment>
      ))}
    </>
  );
};
